import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
  ShoppingCart as ShoppingCartIcon,
  MedicalServices as ServiceIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as ReportIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  DateRange as DateRangeIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Inventory as InventoryIcon,
  Storefront as StorefrontIcon
} from '@mui/icons-material';
import AdminLayout from '../../components/AdminLayout';
import { collection, getDocs, query, orderBy, getFirestore, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import app from '../../config/firebase';
import dayjs from 'dayjs';
import VentasPetshopSection, {
  calcularGananciaPetshop,
  COLECCION_VENTAS_PETSHOP,
  COLECCION_PRODUCTOS_PETSHOP
} from '../../components/VentasPetshopSection';

// Inicialización de Firestore
const db = getFirestore(app);

const Finanzas = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Estados principales
  const [ventasProductos, setVentasProductos] = useState([]);
  const [ventasPetshop, setVentasPetshop] = useState([]);
  const [ventasPetshopFiltradas, setVentasPetshopFiltradas] = useState([]);
  const [productosPetshop, setProductosPetshop] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [productos, setProductos] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados para modales
  const [openProductoModal, setOpenProductoModal] = useState(false);
  const [openServicioModal, setOpenServicioModal] = useState(false);
  const [openEditProductoModal, setOpenEditProductoModal] = useState(false);
  const [openEditServicioModal, setOpenEditServicioModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [itemToView, setItemToView] = useState(null);
  const [viewType, setViewType] = useState('');
  const [loadingVenta, setLoadingVenta] = useState(false);
  const [loadingServicio, setLoadingServicio] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingAdquisicion, setLoadingAdquisicion] = useState(false);

  // Estados para adquisiciones
  const [adquisiciones, setAdquisiciones] = useState([]);
  const [adquisicionesFiltradas, setAdquisicionesFiltradas] = useState([]);
  const [openAdquisicionModal, setOpenAdquisicionModal] = useState(false);
  // Estados multi-ítem para modales de registro
  const [datosVenta, setDatosVenta] = useState({ cliente: '', fecha: dayjs().format('YYYY-MM-DD'), items: [] });
  const [datosServicio, setDatosServicio] = useState({ veterinario: 'Dr. Belkys', fecha: dayjs().format('YYYY-MM-DD'), notas: '', items: [] });
  const [datosAdquisicion, setDatosAdquisicion] = useState({ proveedor: '', fecha: dayjs().format('YYYY-MM-DD'), notas: '', items: [] });
  
  // Estados para edición y eliminación
  const [itemToEdit, setItemToEdit] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'venta' o 'servicio'
  
  // Estados para formularios
  const [ventaProducto, setVentaProducto] = useState({
    productoId: '',
    productoNombre: '',
    cantidad: 1,
    precioUnitario: 0,
    cliente: '',
    fecha: dayjs().format('YYYY-MM-DD'),
    total: 0,
    esProductoPersonalizado: false, // Para productos tipo "Otro"
    nombreProductoPersonalizado: '' // Nombre del producto personalizado
  });
  
  const [nuevoServicio, setNuevoServicio] = useState({
    tipoServicio: '',
    pacienteId: '',
    pacienteNombre: '',
    precio: 0,
    veterinario: 'Dr. Belkys',
    fecha: dayjs().format('YYYY-MM-DD'),
    notas: '',
    descripcionOtro: '' // Para cuando se seleccione "Otro"
  });
  
  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Estados para filtro de fechas
  const [filtroFechas, setFiltroFechas] = useState({
    fechaInicio: '',
    fechaFin: '',
    activo: false
  });
  const [ventasFiltradas, setVentasFiltradas] = useState([]);
  const [serviciosFiltrados, setServiciosFiltrados] = useState([]);

  // Estados para cálculo de crecimiento
  const [datosAnterior, setDatosAnterior] = useState({
    ingresoMesAnterior: 0,
    crecimiento: 0,
    cargando: true
  });

  // Tipos de servicios disponibles
  const tiposServicios = [
    'Consulta General',
    'Vacunación',
    'Desparasitación',
    'Medicación Inyectable',
    'Radiografía',
    'Análisis de Laboratorio',
    'Limpieza',
    'Emergencia',
    'Control Post-operatorio',
    'Otro'
  ];

  useEffect(() => {
    cargarDatos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calcular crecimiento cuando se carguen ventas y servicios (con timeout de seguridad)
  useEffect(() => {
    // Timeout de seguridad para evitar estados de carga infinitos
    const timeoutId = setTimeout(() => {
      console.log('⏰ Timeout de seguridad: finalizando cálculo de crecimiento');
      setDatosAnterior(prev => ({ ...prev, cargando: false }));
    }, 5000); // 5 segundos máximo

    // Calcular crecimiento inmediatamente si hay datos o si ya terminó la carga inicial
    if (!loading) {
      calcularCrecimiento().finally(() => {
        clearTimeout(timeoutId);
      });
    }

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventasProductos, ventasPetshop, servicios, loading]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      console.log('Iniciando carga de datos...');
      console.log('Firebase DB:', db);
      
      // Cargar productos desde Firebase
      await cargarProductos();
      
      // Cargar pacientes desde Firebase
      await cargarPacientes();
      
      // Cargar ventas de productos desde Firebase
      await cargarVentasProductos();

      // Cargar productos y ventas del petshop
      await cargarPetshop();

      // Cargar servicios desde Firebase
      await cargarServicios();

      // Cargar adquisiciones desde Firebase
      await cargarAdquisiciones();
      
    } catch (error) {
      console.error('Error cargando datos:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar los datos',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarProductos = async () => {
    try {
      const productosRef = collection(db, 'inventario');
      const productosSnapshot = await getDocs(productosRef);
      const productosData = productosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProductos(productosData);
    } catch (error) {
      console.error('Error cargando productos:', error);
    }
  };

  const cargarPacientes = async () => {
    try {
      const pacientesRef = collection(db, 'pacientes');
      const pacientesSnapshot = await getDocs(pacientesRef);
      const pacientesData = pacientesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPacientes(pacientesData);
    } catch (error) {
      console.error('Error cargando pacientes:', error);
    }
  };

  const cargarVentasProductos = async () => {
    try {
      const ventasRef = collection(db, 'ventas');
      const ventasQuery = query(ventasRef, orderBy('timestamp', 'desc'));
      const ventasSnapshot = await getDocs(ventasQuery);
      const ventasData = ventasSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          producto: data.productoNombre || data.producto,
          cantidad: data.cantidad,
          precioUnitario: data.precioUnitario,
          total: data.total,
          cliente: data.cliente,
          fecha: data.fecha,
          ...data
        };
      });
      setVentasProductos(ventasData);
    } catch (error) {
      console.error('Error cargando ventas:', error);
      // Si no hay ventas, mostrar datos de ejemplo
      setVentasProductos([
        {
          id: 'ejemplo-1',
          producto: 'Alimento Premium Perro',
          cantidad: 2,
          precioUnitario: 45000,
          total: 90000,
          cliente: 'María González',
          fecha: dayjs().format('YYYY-MM-DD')
        }
      ]);
    }
  };

  const cargarPetshop = async () => {
    try {
      const prodSnap = await getDocs(collection(db, COLECCION_PRODUCTOS_PETSHOP));
      setProductosPetshop(prodSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error cargando productos de petshop:', error);
    }
    try {
      const ventasQuery = query(collection(db, COLECCION_VENTAS_PETSHOP), orderBy('timestamp', 'desc'));
      const ventasSnap = await getDocs(ventasQuery);
      setVentasPetshop(ventasSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error cargando ventas de petshop:', error);
    }
  };

  const cargarServicios = async () => {
    try {
      const serviciosRef = collection(db, 'servicios');
      const serviciosQuery = query(serviciosRef, orderBy('timestamp', 'desc'));
      const serviciosSnapshot = await getDocs(serviciosQuery);
      const serviciosData = serviciosSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          tipoServicio: data.tipoServicio,
          paciente: data.pacienteNombre || data.paciente,
          precio: data.precio,
          veterinario: data.veterinario,
          fecha: data.fecha,
          ...data
        };
      });
      setServicios(serviciosData);
    } catch (error) {
      console.error('Error cargando servicios:', error);
      // Si no hay servicios, mostrar datos de ejemplo
      setServicios([
        {
          id: 'ejemplo-1',
          tipoServicio: 'Consulta General',
          paciente: 'Max',
          precio: 35000,
          veterinario: 'Dr. Belkys',
          fecha: dayjs().format('YYYY-MM-DD')
        }
      ]);
    }
  };

  const cargarAdquisiciones = async () => {
    try {
      const adqRef = collection(db, 'adquisiciones');
      const adqQuery = query(adqRef, orderBy('timestamp', 'desc'));
      const adqSnapshot = await getDocs(adqQuery);
      const adqData = adqSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setAdquisiciones(adqData);
    } catch (error) {
      console.error('Error cargando adquisiciones:', error);
    }
  };

  // ─── Helpers multi-ítem ─────────────────────────────────────────────────────
  const crearItemVenta = () => ({
    id: Date.now() + Math.random(),
    productoId: '', productoNombre: '', cantidad: 1, precioUnitario: 0, total: 0,
    esProductoPersonalizado: false, nombreProductoPersonalizado: ''
  });
  const crearItemServicio = () => ({
    id: Date.now() + Math.random(),
    tipoServicio: '', descripcionOtro: '', pacienteId: '', pacienteNombre: '', precio: 0
  });
  const crearItemAdq = () => ({
    id: Date.now() + Math.random(),
    esNuevoProducto: true, productoId: '', productoNombre: '', cantidad: 1, costoUnitario: 0, costoTotal: 0
  });

  const abrirModalVenta = () => {
    setDatosVenta({ cliente: '', fecha: dayjs().format('YYYY-MM-DD'), items: [crearItemVenta()] });
    setOpenProductoModal(true);
  };
  const abrirModalServicio = () => {
    setDatosServicio({ veterinario: 'Dr. Belkys', fecha: dayjs().format('YYYY-MM-DD'), notas: '', items: [crearItemServicio()] });
    setOpenServicioModal(true);
  };
  const abrirModalAdquisicion = () => {
    setDatosAdquisicion({ proveedor: '', fecha: dayjs().format('YYYY-MM-DD'), notas: '', items: [crearItemAdq()] });
    setOpenAdquisicionModal(true);
  };

  const actualizarItemVenta = (itemId, field, value) => {
    setDatosVenta(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id !== itemId) return item;
        const updated = { ...item, [field]: value };
        if (field === 'productoId') {
          if (value === 'otro') {
            updated.esProductoPersonalizado = true; updated.productoNombre = ''; updated.precioUnitario = 0;
          } else {
            updated.esProductoPersonalizado = false; updated.nombreProductoPersonalizado = '';
            const prod = productos.find(p => p.id === value);
            if (prod) { updated.productoNombre = prod.nombre; updated.precioUnitario = prod.precio || 0; }
          }
        }
        if (field === 'nombreProductoPersonalizado') updated.productoNombre = value;
        updated.total = (parseFloat(updated.cantidad) || 0) * (parseFloat(updated.precioUnitario) || 0);
        return updated;
      })
    }));
  };

  const actualizarItemServicio = (itemId, field, value) => {
    setDatosServicio(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id !== itemId) return item;
        const updated = { ...item, [field]: value };
        if (field === 'pacienteId') {
          const pac = pacientes.find(p => p.id === value);
          if (pac) updated.pacienteNombre = pac.nombre;
        }
        return updated;
      })
    }));
  };

  const actualizarItemAdq = (itemId, field, value) => {
    setDatosAdquisicion(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id !== itemId) return item;
        const updated = { ...item, [field]: value };
        if (field === 'esNuevoProducto') { updated.productoId = ''; updated.productoNombre = ''; updated.costoUnitario = 0; }
        if (field === 'productoId' && value) {
          const prod = productos.find(p => p.id === value);
          if (prod) { updated.productoNombre = prod.nombre; updated.costoUnitario = prod.precioCompra || prod.precio || 0; }
        }
        updated.costoTotal = (parseFloat(updated.cantidad) || 0) * (parseFloat(updated.costoUnitario) || 0);
        return updated;
      })
    }));
  };

  const agregarItemVenta = () => setDatosVenta(prev => ({ ...prev, items: [...prev.items, crearItemVenta()] }));
  const quitarItemVenta = (id) => setDatosVenta(prev => ({ ...prev, items: prev.items.filter(i => i.id !== id) }));
  const agregarItemServicio = () => setDatosServicio(prev => ({ ...prev, items: [...prev.items, crearItemServicio()] }));
  const quitarItemServicio = (id) => setDatosServicio(prev => ({ ...prev, items: prev.items.filter(i => i.id !== id) }));
  const agregarItemAdq = () => setDatosAdquisicion(prev => ({ ...prev, items: [...prev.items, crearItemAdq()] }));
  const quitarItemAdq = (id) => setDatosAdquisicion(prev => ({ ...prev, items: prev.items.filter(i => i.id !== id) }));

  const guardarAdquisicion = async () => {
    if (loadingAdquisicion) return;
    try {
      setLoadingAdquisicion(true);
      if (!datosAdquisicion.fecha || datosAdquisicion.items.length === 0) {
        setSnackbar({ open: true, message: 'Complete la fecha y al menos un producto', severity: 'error' });
        return;
      }
      for (const item of datosAdquisicion.items) {
        if (!item.productoNombre || !item.cantidad || !item.costoUnitario) {
          setSnackbar({ open: true, message: 'Complete todos los campos de cada producto a adquirir', severity: 'error' });
          return;
        }
      }
      for (const item of datosAdquisicion.items) {
        let productoId = item.productoId;
        if (item.esNuevoProducto) {
          const ref = await addDoc(collection(db, 'inventario'), {
            nombre: item.productoNombre,
            cantidad: parseInt(item.cantidad),
            precio: parseFloat(item.costoUnitario),
            precioCompra: parseFloat(item.costoUnitario),
            proveedor: datosAdquisicion.proveedor || '',
            categoria: 'General',
            fechaCreacion: new Date(),
            ultimaActualizacion: new Date()
          });
          productoId = ref.id;
        } else {
          const prod = productos.find(p => p.id === productoId);
          if (prod) {
            await updateDoc(doc(db, 'inventario', productoId), {
              cantidad: (prod.cantidad || 0) + parseInt(item.cantidad),
              precioCompra: parseFloat(item.costoUnitario),
              ultimaActualizacion: new Date()
            });
          }
        }
        await addDoc(collection(db, 'adquisiciones'), {
          productoId,
          productoNombre: item.productoNombre,
          cantidad: parseInt(item.cantidad),
          costoUnitario: parseFloat(item.costoUnitario),
          costoTotal: parseFloat(item.costoTotal),
          proveedor: datosAdquisicion.proveedor || '',
          fecha: datosAdquisicion.fecha,
          notas: datosAdquisicion.notas || '',
          timestamp: new Date(),
          tipo: 'adquisicion_producto',
          esNuevoProducto: item.esNuevoProducto
        });
      }
      await cargarAdquisiciones();
      await cargarProductos();
      setOpenAdquisicionModal(false);
      setSnackbar({
        open: true,
        message: `${datosAdquisicion.items.length} producto${datosAdquisicion.items.length > 1 ? 's' : ''} adquirido${datosAdquisicion.items.length > 1 ? 's' : ''} exitosamente`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error guardando adquisición:', error);
      setSnackbar({ open: true, message: 'Error al registrar la adquisición', severity: 'error' });
    } finally {
      setLoadingAdquisicion(false);
    }
  };

  const abrirEliminarAdquisicion = (adquisicion) => {
    setItemToDelete(adquisicion);
    setDeleteType('adquisicion');
    setOpenDeleteModal(true);
  };

  const handleProductoChange = (field, value) => {
    setVentaProducto(prev => {
      const updated = { ...prev, [field]: value };
      
      // Si se selecciona un producto, actualizar el precio
      if (field === 'productoId') {
        if (value === 'otro') {
          // Producto personalizado
          updated.esProductoPersonalizado = true;
          updated.productoNombre = '';
          updated.precioUnitario = 0;
        } else {
          // Producto del inventario
          updated.esProductoPersonalizado = false;
          updated.nombreProductoPersonalizado = '';
          const producto = productos.find(p => p.id === value);
          if (producto) {
            updated.productoNombre = producto.nombre;
            updated.precioUnitario = producto.precio || 0;
          }
        }
      }
      
      // Si es producto personalizado y se cambia el nombre
      if (field === 'nombreProductoPersonalizado') {
        updated.productoNombre = value;
      }
      
      // Calcular total
      updated.total = updated.cantidad * updated.precioUnitario;
      
      return updated;
    });
  };

  const handleServicioChange = (field, value) => {
    setNuevoServicio(prev => {
      const updated = { ...prev, [field]: value };
      
      // Si se selecciona un paciente, actualizar el nombre
      if (field === 'pacienteId') {
        const paciente = pacientes.find(p => p.id === value);
        if (paciente) {
          updated.pacienteNombre = paciente.nombre;
        }
      }
      
      return updated;
    });
  };

  const guardarVentaProducto = async () => {
    if (loadingVenta) return;
    try {
      setLoadingVenta(true);
      if (!datosVenta.cliente || !datosVenta.fecha || datosVenta.items.length === 0) {
        setSnackbar({ open: true, message: 'Complete el cliente, la fecha y al menos un producto', severity: 'error' });
        return;
      }
      for (const item of datosVenta.items) {
        if (!item.productoId || !item.cantidad) {
          setSnackbar({ open: true, message: 'Complete todos los campos de cada producto', severity: 'error' });
          return;
        }
        if (item.esProductoPersonalizado && !item.nombreProductoPersonalizado) {
          setSnackbar({ open: true, message: 'Ingrese el nombre del producto personalizado', severity: 'error' });
          return;
        }
        if (!item.esProductoPersonalizado) {
          const prod = productos.find(p => p.id === item.productoId);
          if (!prod) { setSnackbar({ open: true, message: 'Producto no encontrado', severity: 'error' }); return; }
          if (parseInt(item.cantidad) > (prod.cantidad || 0)) {
            setSnackbar({ open: true, message: `Stock insuficiente para "${prod.nombre}". Disponible: ${prod.cantidad || 0}`, severity: 'error' });
            return;
          }
        }
      }
      let inventarioActualizado = false;
      for (const item of datosVenta.items) {
        const esPersonalizado = item.productoId === 'otro';
        const prod = !esPersonalizado ? productos.find(p => p.id === item.productoId) : null;
        const precioCompra = prod?.precio || 0;
        await addDoc(collection(db, 'ventas'), {
          productoId: item.productoId,
          productoNombre: item.productoNombre,
          cantidad: parseInt(item.cantidad),
          precioUnitario: parseFloat(item.precioUnitario),
          total: parseFloat(item.total),
          precioCompra,
          cliente: datosVenta.cliente,
          fecha: datosVenta.fecha,
          timestamp: new Date(),
          tipo: 'venta_producto',
          esProductoPersonalizado: esPersonalizado
        });
        if (!esPersonalizado && prod) {
          const nuevoStock = (prod.cantidad || 0) - parseInt(item.cantidad);
          await updateDoc(doc(db, 'inventario', item.productoId), {
            cantidad: nuevoStock,
            ultimaVenta: new Date(),
            ventasRealizadas: (prod.ventasRealizadas || 0) + 1
          });
          inventarioActualizado = true;
        }
      }
      await cargarVentasProductos();
      if (inventarioActualizado) await cargarProductos();
      setOpenProductoModal(false);
      setSnackbar({
        open: true,
        message: `${datosVenta.items.length} producto${datosVenta.items.length > 1 ? 's' : ''} registrado${datosVenta.items.length > 1 ? 's' : ''} exitosamente`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error guardando venta:', error);
      let mensajeError = 'Error al guardar la venta';
      if (error.code === 'permission-denied') mensajeError = 'No tiene permisos para realizar esta operación';
      else if (error.code === 'unavailable') mensajeError = 'Servicio no disponible. Intente nuevamente';
      setSnackbar({ open: true, message: mensajeError, severity: 'error' });
    } finally {
      setLoadingVenta(false);
    }
  };

  const guardarServicio = async () => {
    if (loadingServicio) return;
    try {
      setLoadingServicio(true);
      if (!datosServicio.veterinario || !datosServicio.fecha || datosServicio.items.length === 0) {
        setSnackbar({ open: true, message: 'Complete el veterinario, la fecha y al menos un servicio', severity: 'error' });
        return;
      }
      for (const item of datosServicio.items) {
        if (!item.tipoServicio || !item.precio) {
          setSnackbar({ open: true, message: 'Complete el tipo y precio de cada servicio', severity: 'error' });
          return;
        }
        if (item.tipoServicio !== 'Otro' && !item.pacienteId) {
          setSnackbar({ open: true, message: 'Seleccione un paciente para cada servicio', severity: 'error' });
          return;
        }
      }
      for (const item of datosServicio.items) {
        const servicioData = {
          tipoServicio: item.tipoServicio,
          pacienteId: item.pacienteId || null,
          pacienteNombre: item.pacienteNombre || null,
          precio: parseFloat(item.precio),
          veterinario: datosServicio.veterinario,
          fecha: datosServicio.fecha,
          notas: datosServicio.notas || '',
          timestamp: new Date(),
          tipo: 'servicio_veterinario'
        };
        if (item.tipoServicio === 'Otro') servicioData.descripcionOtro = item.descripcionOtro || '';
        await addDoc(collection(db, 'servicios'), servicioData);
      }
      await cargarServicios();
      setOpenServicioModal(false);
      setSnackbar({
        open: true,
        message: `${datosServicio.items.length} servicio${datosServicio.items.length > 1 ? 's' : ''} registrado${datosServicio.items.length > 1 ? 's' : ''} exitosamente`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error guardando servicio:', error);
      setSnackbar({ open: true, message: 'Error al guardar el servicio', severity: 'error' });
    } finally {
      setLoadingServicio(false);
    }
  };

  // Funciones para edición
  const abrirEditarVenta = (venta) => {
    setItemToEdit(venta);
    const esProductoPersonalizado = venta.esProductoPersonalizado || venta.productoId === 'otro';
    setVentaProducto({
      id: venta.id,
      productoId: venta.productoId || '',
      productoNombre: venta.productoNombre || venta.producto,
      cantidad: venta.cantidad,
      precioUnitario: venta.precioUnitario,
      cliente: venta.cliente,
      fecha: venta.fecha,
      total: venta.total,
      esProductoPersonalizado: esProductoPersonalizado,
      nombreProductoPersonalizado: esProductoPersonalizado ? venta.productoNombre : ''
    });
    setOpenEditProductoModal(true);
  };

  const abrirEditarServicio = (servicio) => {
    setItemToEdit(servicio);
    setNuevoServicio({
      id: servicio.id,
      tipoServicio: servicio.tipoServicio,
      pacienteId: servicio.pacienteId || '',
      pacienteNombre: servicio.pacienteNombre || servicio.paciente,
      precio: servicio.precio,
      veterinario: servicio.veterinario,
      fecha: servicio.fecha,
      notas: servicio.notas || '',
      descripcionOtro: servicio.descripcionOtro || ''
    });
    setOpenEditServicioModal(true);
  };

  const editarVentaProducto = async () => {
    if (loadingVenta) return;
    
    try {
      setLoadingVenta(true);
      
      // Validaciones
      if (!ventaProducto.productoId || !ventaProducto.cantidad || !ventaProducto.cliente) {
        setSnackbar({
          open: true,
          message: 'Por favor complete todos los campos obligatorios',
          severity: 'error'
        });
        return;
      }

      // Calcular diferencia de stock
      const ventaOriginal = itemToEdit;
      const diferenciaCantidad = parseInt(ventaProducto.cantidad) - ventaOriginal.cantidad;
      const esProductoPersonalizado = ventaProducto.esProductoPersonalizado || ventaProducto.productoId === 'otro';
      
      // Si aumentó la cantidad y NO es producto personalizado, verificar stock disponible
      if (diferenciaCantidad > 0 && !esProductoPersonalizado) {
        const producto = productos.find(p => p.id === ventaProducto.productoId);
        const stockDisponible = producto?.cantidad || 0;
        
        if (diferenciaCantidad > stockDisponible) {
          setSnackbar({
            open: true,
            message: `Stock insuficiente. Disponible: ${stockDisponible} unidades`,
            severity: 'error'
          });
          return;
        }
      }

      // Obtener precio de compra del producto (para calcular ganancia)
      let precioCompraEdit = 0;
      if (!esProductoPersonalizado) {
        const productoInventario = productos.find(p => p.id === ventaProducto.productoId);
        if (productoInventario) {
          precioCompraEdit = productoInventario.precio || 0;
        }
      }

      // Actualizar documento de venta
      const ventaActualizada = {
        productoId: ventaProducto.productoId,
        productoNombre: ventaProducto.productoNombre,
        cantidad: parseInt(ventaProducto.cantidad),
        precioUnitario: parseFloat(ventaProducto.precioUnitario),
        total: parseFloat(ventaProducto.total),
        precioCompra: precioCompraEdit,
        cliente: ventaProducto.cliente,
        fecha: ventaProducto.fecha,
        fechaModificacion: new Date(),
        tipo: 'venta_producto',
        esProductoPersonalizado: esProductoPersonalizado
      };

      // Actualizar en Firebase
      const ventaRef = doc(db, 'ventas', ventaProducto.id);
      await updateDoc(ventaRef, ventaActualizada);

      // Actualizar inventario si cambió la cantidad y NO es producto personalizado
      if (diferenciaCantidad !== 0 && !esProductoPersonalizado) {
        const productoRef = doc(db, 'inventario', ventaProducto.productoId);
        const producto = productos.find(p => p.id === ventaProducto.productoId);
        const nuevoStock = (producto?.cantidad || 0) - diferenciaCantidad;
        
        await updateDoc(productoRef, {
          cantidad: nuevoStock,
          ultimaModificacion: new Date()
        });
      }

      // Recargar datos
      await cargarVentasProductos();
      if (!esProductoPersonalizado) {
        await cargarProductos();
      }

      // Cerrar modal y resetear
      setOpenEditProductoModal(false);
      setItemToEdit(null);
      setVentaProducto({
        productoId: '',
        productoNombre: '',
        cantidad: 1,
        precioUnitario: 0,
        cliente: '',
        fecha: dayjs().format('YYYY-MM-DD'),
        total: 0,
        esProductoPersonalizado: false,
        nombreProductoPersonalizado: ''
      });

      setSnackbar({
        open: true,
        message: 'Venta actualizada exitosamente',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error editando venta:', error);
      setSnackbar({
        open: true,
        message: 'Error al actualizar la venta',
        severity: 'error'
      });
    } finally {
      setLoadingVenta(false);
    }
  };

  const editarServicio = async () => {
    if (loadingServicio) return;
    
    try {
      setLoadingServicio(true);
      
      // Validaciones
      const esTipoOtro = nuevoServicio.tipoServicio === 'Otro';
      const camposObligatorios = esTipoOtro 
        ? !nuevoServicio.tipoServicio || !nuevoServicio.precio
        : !nuevoServicio.tipoServicio || !nuevoServicio.pacienteId || !nuevoServicio.precio;
        
      if (camposObligatorios) {
        setSnackbar({
          open: true,
          message: 'Por favor complete todos los campos obligatorios',
          severity: 'error'
        });
        return;
      }

      // Actualizar documento de servicio
      const servicioActualizado = {
        tipoServicio: nuevoServicio.tipoServicio,
        pacienteId: nuevoServicio.pacienteId || null,
        pacienteNombre: nuevoServicio.pacienteNombre || null,
        precio: parseFloat(nuevoServicio.precio),
        veterinario: nuevoServicio.veterinario || 'Dr. Belkys',
        fecha: nuevoServicio.fecha,
        notas: nuevoServicio.notas,
        fechaModificacion: new Date(),
        tipo: 'servicio_veterinario'
      };

      // Agregar descripción si es tipo "Otro"
      if (nuevoServicio.tipoServicio === 'Otro') {
        servicioActualizado.descripcionOtro = nuevoServicio.descripcionOtro;
      }

      // Actualizar en Firebase
      const servicioRef = doc(db, 'servicios', nuevoServicio.id);
      await updateDoc(servicioRef, servicioActualizado);

      // Recargar datos
      await cargarServicios();

      // Cerrar modal y resetear
      setOpenEditServicioModal(false);
      setItemToEdit(null);
      setNuevoServicio({
        tipoServicio: '',
        pacienteId: '',
        pacienteNombre: '',
        precio: 0,
        veterinario: 'Dr. Belkys',
        fecha: dayjs().format('YYYY-MM-DD'),
        notas: '',
        descripcionOtro: ''
      });

      setSnackbar({
        open: true,
        message: 'Servicio actualizado exitosamente',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error editando servicio:', error);
      setSnackbar({
        open: true,
        message: 'Error al actualizar el servicio',
        severity: 'error'
      });
    } finally {
      setLoadingServicio(false);
    }
  };

  // Funciones para eliminación
  const abrirVerVenta = (venta) => {
    setItemToView(venta);
    setViewType('venta');
    setOpenViewModal(true);
  };

  const abrirVerServicio = (servicio) => {
    setItemToView(servicio);
    setViewType('servicio');
    setOpenViewModal(true);
  };

  const abrirEliminarVenta = (venta) => {
    setItemToDelete(venta);
    setDeleteType('venta');
    setOpenDeleteModal(true);
  };

  const abrirEliminarServicio = (servicio) => {
    setItemToDelete(servicio);
    setDeleteType('servicio');
    setOpenDeleteModal(true);
  };

  const confirmarEliminacion = async () => {
    if (loadingDelete) return;
    
    try {
      setLoadingDelete(true);

      if (deleteType === 'venta') {
        // Eliminar venta
        const ventaRef = doc(db, 'ventas', itemToDelete.id);
        await deleteDoc(ventaRef);

        // Restaurar stock al inventario solo si NO es un producto personalizado
        const esProductoPersonalizado = itemToDelete.esProductoPersonalizado || itemToDelete.productoId === 'otro';
        
        if (itemToDelete.productoId && !esProductoPersonalizado) {
          const productoRef = doc(db, 'inventario', itemToDelete.productoId);
          const producto = productos.find(p => p.id === itemToDelete.productoId);
          const nuevoStock = (producto?.cantidad || 0) + itemToDelete.cantidad;
          
          await updateDoc(productoRef, {
            cantidad: nuevoStock,
            ultimaModificacion: new Date()
          });
          
          setSnackbar({
            open: true,
            message: 'Venta eliminada exitosamente. Stock restaurado',
            severity: 'success'
          });
        } else {
          // Mensaje para producto personalizado
          setSnackbar({
            open: true,
            message: 'Venta de producto personalizado eliminada exitosamente',
            severity: 'success'
          });
        }

        await cargarVentasProductos();
        if (!esProductoPersonalizado) {
          await cargarProductos(); // Solo recargar productos si no es personalizado
        }
        
      } else if (deleteType === 'servicio') {
        // Eliminar servicio
        const servicioRef = doc(db, 'servicios', itemToDelete.id);
        await deleteDoc(servicioRef);
        
        await cargarServicios();
        
        setSnackbar({
          open: true,
          message: 'Servicio eliminado exitosamente',
          severity: 'success'
        });
      } else if (deleteType === 'adquisicion') {
        // Eliminar adquisición y revertir stock
        const adqRef = doc(db, 'adquisiciones', itemToDelete.id);
        await deleteDoc(adqRef);

        if (itemToDelete.productoId) {
          const producto = productos.find(p => p.id === itemToDelete.productoId);
          if (producto) {
            const nuevoStock = Math.max(0, (producto.cantidad || 0) - itemToDelete.cantidad);
            await updateDoc(doc(db, 'inventario', itemToDelete.productoId), {
              cantidad: nuevoStock,
              ultimaModificacion: new Date()
            });
            await cargarProductos();
          }
        }

        await cargarAdquisiciones();
        setSnackbar({
          open: true,
          message: 'Adquisición eliminada y stock ajustado',
          severity: 'success'
        });
      }

      // Cerrar modal y resetear
      setOpenDeleteModal(false);
      setItemToDelete(null);
      setDeleteType('');

    } catch (error) {
      console.error('Error eliminando registro:', error);
      setSnackbar({
        open: true,
        message: 'Error al eliminar el registro',
        severity: 'error'
      });
    } finally {
      setLoadingDelete(false);
    }
  };

  // Funciones para filtrado por fechas
  const aplicarFiltroFechas = () => {
    if (!filtroFechas.fechaInicio || !filtroFechas.fechaFin) {
      setSnackbar({
        open: true,
        message: 'Por favor selecciona ambas fechas',
        severity: 'warning'
      });
      return;
    }

    const fechaInicio = new Date(filtroFechas.fechaInicio);
    const fechaFin = new Date(filtroFechas.fechaFin);
    fechaFin.setHours(23, 59, 59, 999); // Incluir todo el día final

    if (fechaInicio > fechaFin) {
      setSnackbar({
        open: true,
        message: 'La fecha de inicio debe ser anterior a la fecha de fin',
        severity: 'warning'
      });
      return;
    }

    const ventasFiltradas = ventasProductos.filter(venta => {
      const fechaVenta = new Date(venta.fecha);
      return fechaVenta >= fechaInicio && fechaVenta <= fechaFin;
    });

    const petshopFiltradas = ventasPetshop.filter(venta => {
      const fechaVenta = new Date(venta.fecha);
      return fechaVenta >= fechaInicio && fechaVenta <= fechaFin;
    });

    const serviciosFiltrados = servicios.filter(servicio => {
      const fechaServicio = new Date(servicio.fecha);
      return fechaServicio >= fechaInicio && fechaServicio <= fechaFin;
    });

    const adqFiltradas = adquisiciones.filter(adq => {
      const fechaAdq = new Date(adq.fecha);
      return fechaAdq >= fechaInicio && fechaAdq <= fechaFin;
    });

    setVentasFiltradas(ventasFiltradas);
    setVentasPetshopFiltradas(petshopFiltradas);
    setServiciosFiltrados(serviciosFiltrados);
    setAdquisicionesFiltradas(adqFiltradas);
    setFiltroFechas(prev => ({ ...prev, activo: true }));

    setSnackbar({
      open: true,
      message: `Filtro aplicado: ${ventasFiltradas.length} ventas, ${petshopFiltradas.length} ventas de petshop, ${serviciosFiltrados.length} servicios y ${adqFiltradas.length} adquisiciones encontrados`,
      severity: 'success'
    });
  };

  const limpiarFiltroFechas = () => {
    setFiltroFechas({
      fechaInicio: '',
      fechaFin: '',
      activo: false
    });
    setVentasFiltradas([]);
    setVentasPetshopFiltradas([]);
    setServiciosFiltrados([]);
    setAdquisicionesFiltradas([]);

    setSnackbar({
      open: true,
      message: 'Filtro de fechas eliminado. Mostrando mes actual',
      severity: 'info'
    });
  };

  // Función para calcular crecimiento comparando con mes anterior (optimizada)
  const calcularCrecimiento = async () => {
    // Marcar como cargando al inicio
    setDatosAnterior(prev => ({ ...prev, cargando: true }));
    
    try {
      console.log('📊 Calculando crecimiento...');
      
      // Si no hay datos, mostrar resultado inmediatamente
      if (ventasProductos.length === 0 && ventasPetshop.length === 0 && servicios.length === 0) {
        console.log('📊 Sin datos para calcular crecimiento');
        setDatosAnterior({
          ingresoMesAnterior: 0,
          crecimiento: 0,
          cargando: false
        });
        return;
      }

      // Obtener fecha actual
      const ahora = dayjs();
      const inicioMesActual = ahora.startOf('month');
      const finMesActual = ahora.endOf('month');
      
      // Obtener fechas del mes anterior
      const inicioMesAnterior = ahora.subtract(1, 'month').startOf('month');
      const finMesAnterior = ahora.subtract(1, 'month').endOf('month');

      // Calcular ingresos del mes actual (incluir fechas límite)
      const ventasMesActual = ventasProductos.filter(venta => {
        const fechaVenta = dayjs(venta.fecha);
        return fechaVenta.isValid() && fechaVenta.valueOf() >= inicioMesActual.valueOf() && fechaVenta.valueOf() <= finMesActual.valueOf();
      });

      const petshopMesActual = ventasPetshop.filter(venta => {
        const fechaVenta = dayjs(venta.fecha);
        return fechaVenta.isValid() && fechaVenta.valueOf() >= inicioMesActual.valueOf() && fechaVenta.valueOf() <= finMesActual.valueOf();
      });

      const petshopMesAnterior = ventasPetshop.filter(venta => {
        const fechaVenta = dayjs(venta.fecha);
        return fechaVenta.isValid() && fechaVenta.valueOf() >= inicioMesAnterior.valueOf() && fechaVenta.valueOf() <= finMesAnterior.valueOf();
      });

      const serviciosMesActual = servicios.filter(servicio => {
        const fechaServicio = dayjs(servicio.fecha);
        return fechaServicio.isValid() && fechaServicio.valueOf() >= inicioMesActual.valueOf() && fechaServicio.valueOf() <= finMesActual.valueOf();
      });

      // Calcular ingresos del mes anterior (incluir fechas límite)
      const ventasMesAnterior = ventasProductos.filter(venta => {
        const fechaVenta = dayjs(venta.fecha);
        return fechaVenta.isValid() && fechaVenta.valueOf() >= inicioMesAnterior.valueOf() && fechaVenta.valueOf() <= finMesAnterior.valueOf();
      });

      const serviciosMesAnterior = servicios.filter(servicio => {
        const fechaServicio = dayjs(servicio.fecha);
        return fechaServicio.isValid() && fechaServicio.valueOf() >= inicioMesAnterior.valueOf() && fechaServicio.valueOf() <= finMesAnterior.valueOf();
      });

      // Calcular totales con validación
      const ingresoMesActual = 
        ventasMesActual.reduce((sum, venta) => sum + (venta.total || 0), 0) +
        petshopMesActual.reduce((sum, venta) => sum + (venta.total || 0), 0) +
        serviciosMesActual.reduce((sum, servicio) => sum + (servicio.precio || 0), 0);

      const ingresoMesAnterior = 
        ventasMesAnterior.reduce((sum, venta) => sum + (venta.total || 0), 0) +
        petshopMesAnterior.reduce((sum, venta) => sum + (venta.total || 0), 0) +
        serviciosMesAnterior.reduce((sum, servicio) => sum + (servicio.precio || 0), 0);

      // Calcular porcentaje de crecimiento
      let porcentajeCrecimiento = 0;
      if (ingresoMesAnterior > 0) {
        porcentajeCrecimiento = ((ingresoMesActual - ingresoMesAnterior) / ingresoMesAnterior) * 100;
      } else if (ingresoMesActual > 0) {
        porcentajeCrecimiento = 100; // Si no había ingresos el mes anterior pero hay este mes
      }

      console.log(`✅ Crecimiento calculado: ${porcentajeCrecimiento.toFixed(1)}%`);
      console.log(`💰 Mes actual: $${ingresoMesActual}, Mes anterior: $${ingresoMesAnterior}`);

      setDatosAnterior({
        ingresoMesAnterior,
        crecimiento: porcentajeCrecimiento,
        cargando: false
      });

    } catch (error) {
      console.error('❌ Error calculando crecimiento:', error);
      setDatosAnterior({
        ingresoMesAnterior: 0,
        crecimiento: 0,
        cargando: false
      });
    }
  };

  // Obtener datos a mostrar: por defecto solo mes actual (reinicio mensual visual)
  const filtrarMesActual = (items) => {
    const inicioMes = dayjs().startOf('month');
    const finMes = dayjs().endOf('month');

    return items.filter((item) => {
      const fecha = dayjs(item.fecha);
      return fecha.isValid() && fecha.valueOf() >= inicioMes.valueOf() && fecha.valueOf() <= finMes.valueOf();
    });
  };

  const getVentasAMostrar = () => {
    if (filtroFechas.activo) return ventasFiltradas;
    return filtrarMesActual(ventasProductos);
  };

  const getVentasPetshopAMostrar = () => {
    if (filtroFechas.activo) return ventasPetshopFiltradas;
    return filtrarMesActual(ventasPetshop);
  };

  const getServiciosAMostrar = () => {
    if (filtroFechas.activo) return serviciosFiltrados;
    return filtrarMesActual(servicios);
  };

  const getAdquisicionesAMostrar = () => {
    if (filtroFechas.activo) return adquisicionesFiltradas;
    return filtrarMesActual(adquisiciones);
  };

  // Calcular ganancia por venta individual
  // Obtener costo unitario de compra de una venta
  const getCostoUnitario = (venta) => {
    if (venta.precioCompra !== undefined && venta.precioCompra !== null) {
      return venta.precioCompra;
    }
    if (venta.productoId && venta.productoId !== 'otro') {
      const producto = productos.find(p => p.id === venta.productoId);
      if (producto && producto.precio !== undefined) {
        return producto.precio;
      }
    }
    return null; // desconocido (producto personalizado sin costo)
  };

  const calcularGananciaVenta = (venta) => {
    const costo = getCostoUnitario(venta);
    if (costo !== null) {
      return venta.total - (costo * venta.cantidad);
    }
    // Para productos personalizados sin costo conocido, ganancia = total
    return venta.total;
  };

  // Cálculos de resumen
  const calcularResumen = () => {
    const ventasAUsar = getVentasAMostrar();
    const petshopAUsar = getVentasPetshopAMostrar();
    const serviciosAUsar = getServiciosAMostrar();
    const adqAUsar = getAdquisicionesAMostrar();

    const totalVentas = ventasAUsar.reduce((sum, venta) => sum + venta.total, 0);
    const totalPetshop = petshopAUsar.reduce((sum, venta) => sum + (venta.total || 0), 0);
    const totalServicios = serviciosAUsar.reduce((sum, servicio) => sum + servicio.precio, 0);
    const totalAdquisiciones = adqAUsar.reduce((sum, adq) => sum + (adq.costoTotal || 0), 0);
    const ingresoTotal = totalVentas + totalPetshop + totalServicios - totalAdquisiciones;

    // Ganancias: para ventas = total - costo de compra, para servicios = precio completo, menos adquisiciones
    const gananciaVentas = ventasAUsar.reduce((sum, venta) => sum + calcularGananciaVenta(venta), 0);
    const gananciaPetshop = petshopAUsar.reduce((sum, venta) => sum + calcularGananciaPetshop(venta), 0);
    const gananciaServicios = serviciosAUsar.reduce((sum, servicio) => sum + servicio.precio, 0);
    const totalGanancias = gananciaVentas + gananciaPetshop + gananciaServicios - totalAdquisiciones;

    return {
      ingresoTotal,
      totalVentas,
      totalPetshop,
      cantidadPetshop: petshopAUsar.length,
      totalServicios,
      totalGanancias,
      totalAdquisiciones,
      cantidadVentas: ventasAUsar.length,
      cantidadServicios: serviciosAUsar.length,
      cantidadAdquisiciones: adqAUsar.length
    };
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor);
  };

  const resumen = calcularResumen();

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Container maxWidth="lg" sx={{ mt: 0, mb: 2, px: isMobile ? 0.5 : 1 }}>
        <Paper sx={{ p: isMobile ? 1 : 2, mb: 2, mx: isMobile ? 0.5 : 0 }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold', 
              textAlign: 'center', 
              color: '#c46494',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: isMobile ? 2 : 3,
              fontSize: isMobile ? '1.3rem' : '2rem'
            }}
          >
            <MoneyIcon sx={{ 
              mr: isMobile ? 0.5 : 1, 
              color: '#602442',
              fontSize: isMobile ? '1.5rem' : '2rem'
            }} /> 
            Gestión Financiera 
          </Typography>

          {/* Filtro de Fechas */}
          <Paper sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: 4, 
            border: '1px solid #e0e0e0',
            backgroundColor: '#fafafa' 
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              flexWrap: 'wrap',
              gap: 2
            }}>
              <DateRangeIcon sx={{ color: '#c46494', fontSize: '1.5rem' }} />
              <Typography variant="h6" sx={{ 
                color: '#c46494', 
                fontWeight: 'bold',
                flexGrow: 1,
                minWidth: 'fit-content'
              }}>
                Filtrar por Fechas
              </Typography>
              {filtroFechas.activo && (
                <Chip 
                  label="Filtro Activo"
                  color="primary"
                  size="small"
                  icon={<FilterListIcon />}
                />
              )}
            </Box>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Fecha Inicio"
                  type="date"
                  value={filtroFechas.fechaInicio}
                  onChange={(e) => setFiltroFechas(prev => ({ 
                    ...prev, 
                    fechaInicio: e.target.value 
                  }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Fecha Fin"
                  type="date"
                  value={filtroFechas.fechaFin}
                  onChange={(e) => setFiltroFechas(prev => ({ 
                    ...prev, 
                    fechaFin: e.target.value 
                  }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  onClick={aplicarFiltroFechas}
                  startIcon={<FilterListIcon />}
                  fullWidth
                  sx={{
                    backgroundColor: '#c46494',
                    '&:hover': { backgroundColor: '#a0537a' },
                    height: isMobile ? '40px' : '56px'
                  }}
                >
                  Filtrar
                </Button>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  onClick={limpiarFiltroFechas}
                  startIcon={<ClearIcon />}
                  fullWidth
                  sx={{
                    borderColor: '#c46494',
                    color: '#c46494',
                    '&:hover': { 
                      borderColor: '#a0537a',
                      backgroundColor: '#fce4ec'
                    },
                    height: isMobile ? '40px' : '56px'
                  }}
                >
                  Limpiar
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Resumen Financiero */}
          <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={12} md={4}>
              <Card sx={{ 
                border: '1px solid #4caf50', 
                backgroundColor: '#e8f5e9',
                height: '100%',
                textAlign: 'center'
              }}>
                <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: isMobile ? 0.5 : 1,
                    minHeight: isMobile ? '24px' : '32px'
                  }}>
                    <TrendingUpIcon sx={{ 
                      color: '#4caf50', 
                      mr: isMobile ? 0.5 : 0.75,
                      fontSize: isMobile ? '1rem' : '1.25rem'
                    }} />
                    <Typography 
                      variant={isMobile ? "caption" : "body1"} 
                      sx={{ 
                        color: '#2e7d32', 
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.75rem' : '0.9rem'
                      }}
                    >
                      Ingresos Totales
                    </Typography>
                  </Box>
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ 
                      color: '#2e7d32', 
                      fontWeight: 'bold',
                      fontSize: isMobile ? '1rem' : '1.5rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '28px' : '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {formatearMoneda(resumen.ingresoTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={4}>
              <Card sx={{ 
                border: '1px solid #ff9800', 
                backgroundColor: '#fff3e0',
                height: '100%',
                textAlign: 'center'
              }}>
                <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: isMobile ? 0.5 : 1,
                    minHeight: isMobile ? '24px' : '32px'
                  }}>
                    <MoneyIcon sx={{ 
                      color: '#ff9800', 
                      mr: isMobile ? 0.5 : 0.75,
                      fontSize: isMobile ? '1rem' : '1.25rem'
                    }} />
                    <Typography 
                      variant={isMobile ? "caption" : "body1"} 
                      sx={{ 
                        color: '#e65100', 
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.75rem' : '0.9rem'
                      }}
                    >
                      💰 Ganancias
                    </Typography>
                  </Box>
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ 
                      color: '#e65100', 
                      fontWeight: 'bold',
                      fontSize: isMobile ? '1rem' : '1.5rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '28px' : '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {formatearMoneda(resumen.totalGanancias)}
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    sx={{ 
                      color: '#e65100', 
                      mt: 0.5,
                      fontSize: isMobile ? '0.65rem' : '0.8rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '18px' : '22px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    Ingresos - Costo de productos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={4}>
              <Card sx={{ 
                border: datosAnterior.crecimiento >= 0 ? '1px solid #4caf50' : '1px solid #f44336', 
                backgroundColor: datosAnterior.crecimiento >= 0 ? '#e8f5e9' : '#ffebee',
                height: '100%',
                textAlign: 'center'
              }}>
                <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: isMobile ? 0.5 : 1,
                    minHeight: isMobile ? '24px' : '32px'
                  }}>
                    <ReportIcon sx={{ 
                      color: datosAnterior.crecimiento >= 0 ? '#4caf50' : '#f44336', 
                      mr: isMobile ? 0.5 : 0.75,
                      fontSize: isMobile ? '1rem' : '1.25rem'
                    }} />
                    <Typography 
                      variant={isMobile ? "caption" : "body1"} 
                      sx={{ 
                        color: datosAnterior.crecimiento >= 0 ? '#2e7d32' : '#c62828', 
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.75rem' : '0.9rem'
                      }}
                    >
                      Crecimiento
                    </Typography>
                  </Box>
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ 
                      color: datosAnterior.crecimiento >= 0 ? '#4caf50' : '#f44336', 
                      fontWeight: 'bold',
                      fontSize: isMobile ? '1rem' : '1.5rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '28px' : '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {datosAnterior.cargando ? (
                      <CircularProgress size={isMobile ? 16 : 20} sx={{ color: '#f57c00' }} />
                    ) : datosAnterior.ingresoMesAnterior === 0 && datosAnterior.crecimiento === 0 ? (
                      'N/A'
                    ) : (
                      `${datosAnterior.crecimiento >= 0 ? '+' : ''}${datosAnterior.crecimiento.toFixed(1)}%`
                    )}
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    sx={{ 
                      color: datosAnterior.crecimiento >= 0 ? '#2e7d32' : '#c62828', 
                      mt: 0.5,
                      fontSize: isMobile ? '0.65rem' : '0.8rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '18px' : '22px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {datosAnterior.cargando ? 
                      'Calculando...' : 
                      datosAnterior.ingresoMesAnterior === 0 && datosAnterior.crecimiento === 0 ? 
                        'Sin datos previos' : 
                        'vs mes anterior'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ 
                border: '1px solid #f44336', 
                backgroundColor: '#ffebee',
                height: '100%',
                textAlign: 'center'
              }}>
                <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: isMobile ? 0.5 : 1,
                    minHeight: isMobile ? '24px' : '32px'
                  }}>
                    <AddShoppingCartIcon sx={{ 
                      color: '#f44336', 
                      mr: isMobile ? 0.5 : 0.75,
                      fontSize: isMobile ? '1rem' : '1.25rem'
                    }} />
                    <Typography 
                      variant={isMobile ? "caption" : "body1"} 
                      sx={{ 
                        color: '#c62828', 
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.75rem' : '0.9rem'
                      }}
                    >
                      🛒 Gastos en Adquisiciones
                    </Typography>
                  </Box>
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ 
                      color: '#c62828', 
                      fontWeight: 'bold',
                      fontSize: isMobile ? '1rem' : '1.5rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '28px' : '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {formatearMoneda(resumen.totalAdquisiciones)}
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    sx={{ 
                      color: '#c62828', 
                      mt: 0.5,
                      fontSize: isMobile ? '0.65rem' : '0.8rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '18px' : '22px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {resumen.cantidadAdquisiciones} adquisición(es)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ 
                border: '1px solid #2196f3', 
                backgroundColor: '#e3f2fd',
                height: '100%',
                textAlign: 'center'
              }}>
                <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: isMobile ? 0.5 : 1,
                    minHeight: isMobile ? '24px' : '32px'
                  }}>
                    <ShoppingCartIcon sx={{ 
                      color: '#2196f3', 
                      mr: isMobile ? 0.5 : 0.75,
                      fontSize: isMobile ? '1rem' : '1.25rem'
                    }} />
                    <Typography 
                      variant={isMobile ? "caption" : "body1"} 
                      sx={{ 
                        color: '#1976d2', 
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.75rem' : '0.9rem'
                      }}
                    >
                      Ventas Productos
                    </Typography>
                  </Box>
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ 
                      color: '#1976d2', 
                      fontWeight: 'bold',
                      fontSize: isMobile ? '1rem' : '1.5rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '28px' : '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {formatearMoneda(resumen.totalVentas)}
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    sx={{ 
                      color: '#1976d2', 
                      mt: 0.5,
                      fontSize: isMobile ? '0.65rem' : '0.8rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '18px' : '22px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {resumen.cantidadVentas} ventas realizadas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ 
                border: '1px solid #9c27b0', 
                backgroundColor: '#f3e5f5',
                height: '100%',
                textAlign: 'center'
              }}>
                <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mb: isMobile ? 0.5 : 1,
                    minHeight: isMobile ? '24px' : '32px'
                  }}>
                    <ServiceIcon sx={{ 
                      color: '#9c27b0', 
                      mr: isMobile ? 0.5 : 0.75,
                      fontSize: isMobile ? '1rem' : '1.25rem'
                    }} />
                    <Typography 
                      variant={isMobile ? "caption" : "body1"} 
                      sx={{ 
                        color: '#7b1fa2', 
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.75rem' : '0.9rem'
                      }}
                    >
                      Servicios
                    </Typography>
                  </Box>
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ 
                      color: '#7b1fa2', 
                      fontWeight: 'bold',
                      fontSize: isMobile ? '1rem' : '1.5rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '28px' : '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {formatearMoneda(resumen.totalServicios)}
                  </Typography>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    sx={{ 
                      color: '#7b1fa2', 
                      mt: 0.5,
                      fontSize: isMobile ? '0.65rem' : '0.8rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '18px' : '22px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {resumen.cantidadServicios} servicios realizados
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{
                border: '1px solid #00897b',
                backgroundColor: '#e0f2f1',
                height: '100%',
                textAlign: 'center'
              }}>
                <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: isMobile ? 0.5 : 1,
                    minHeight: isMobile ? '24px' : '32px'
                  }}>
                    <StorefrontIcon sx={{
                      color: '#00897b',
                      mr: isMobile ? 0.5 : 0.75,
                      fontSize: isMobile ? '1rem' : '1.25rem'
                    }} />
                    <Typography
                      variant={isMobile ? "caption" : "body1"}
                      sx={{
                        color: '#00695c',
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.75rem' : '0.9rem'
                      }}
                    >
                      Ventas Petshop
                    </Typography>
                  </Box>
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    sx={{
                      color: '#00695c',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '1rem' : '1.5rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '28px' : '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {formatearMoneda(resumen.totalPetshop)}
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    sx={{
                      color: '#00695c',
                      mt: 0.5,
                      fontSize: isMobile ? '0.65rem' : '0.8rem',
                      textAlign: 'center',
                      minHeight: isMobile ? '18px' : '22px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {resumen.cantidadPetshop} ventas realizadas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Sección de Ventas de Productos */}
          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid item xs={12}>
              <Paper sx={{ 
                p: isMobile ? 1 : 1.5, 
                minHeight: isMobile ? 'auto' : '300px',
                height: 'auto',
                mb: isMobile ? 1.5 : 2
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'flex-start' : 'center', 
                  mb: 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 0
                }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ fontWeight: 'bold', color: '#2196f3' }}
                  >
                    Ventas de Productos
                  </Typography>
                  <Button
                    variant="contained"
                    size={isMobile ? "medium" : "small"}
                    startIcon={<AddIcon />}
                    onClick={abrirModalVenta}
                    fullWidth={isMobile}
                    sx={{
                      backgroundColor: '#2196f3',
                      '&:hover': { backgroundColor: '#1976d2' }
                    }}
                  >
                    Agregar Venta
                  </Button>
                </Box>
                
                {/* Vista Desktop - Tabla */}
                {!isMobile ? (
                  <TableContainer sx={{ 
                    maxHeight: isMobile ? 'none' : '250px',
                    overflowX: 'auto'
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: '120px' }}><strong>Producto</strong></TableCell>
                          <TableCell sx={{ minWidth: '80px', textAlign: 'center' }}><strong>Cant.</strong></TableCell>
                          <TableCell sx={{ minWidth: '100px', textAlign: 'right' }}><strong>Total</strong></TableCell>
                          <TableCell sx={{ minWidth: '100px', textAlign: 'right' }}><strong>Ganancia</strong></TableCell>
                          <TableCell sx={{ minWidth: '120px' }}><strong>Cliente</strong></TableCell>
                          <TableCell sx={{ minWidth: '100px', textAlign: 'center' }}><strong>Fecha</strong></TableCell>
                          <TableCell sx={{ minWidth: '140px', textAlign: 'center' }}><strong>Acciones</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getVentasAMostrar().map((venta) => (
                          <TableRow key={venta.id}>
                            <TableCell sx={{ 
                              fontSize: '0.875rem',
                              wordBreak: 'break-word'
                            }}>
                              {venta.producto}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              {venta.cantidad}
                            </TableCell>
                            <TableCell sx={{ 
                              textAlign: 'right',
                              fontWeight: 'bold',
                              color: '#2196f3'
                            }}>
                              {formatearMoneda(venta.total)}
                            </TableCell>
                            <TableCell sx={{ 
                              textAlign: 'right',
                              fontWeight: 'bold',
                              color: '#e65100'
                            }}>
                              {formatearMoneda(calcularGananciaVenta(venta))}
                            </TableCell>
                            <TableCell sx={{ 
                              fontSize: '0.875rem',
                              wordBreak: 'break-word'
                            }}>
                              {venta.cliente}
                            </TableCell>
                            <TableCell sx={{ 
                              textAlign: 'center',
                              fontSize: '0.875rem'
                            }}>
                              {dayjs(venta.fecha).format('DD/MM/YYYY')}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 0.5,
                                justifyContent: 'center'
                              }}>
                                <Tooltip title="Ver detalle">
                                  <IconButton
                                    size="small"
                                    onClick={() => abrirVerVenta(venta)}
                                    sx={{ color: '#c46494' }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() => abrirEditarVenta(venta)}
                                    sx={{ color: '#2196f3' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton
                                    size="small"
                                    onClick={() => abrirEliminarVenta(venta)}
                                    sx={{ color: '#f44336' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  /* Vista Mobile - Cards */
                  <Box sx={{ 
                    maxHeight: '340px',
                    overflowY: 'auto',
                    pr: 0.5
                  }}>
                    {getVentasAMostrar().map((venta) => (
                      <Card key={venta.id} sx={{ 
                        mb: 1.5, 
                        border: '1px solid #e0e0e0',
                        borderLeft: '4px solid #2196f3'
                      }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 1
                          }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 'bold', 
                                  color: '#2196f3',
                                  lineHeight: 1.2,
                                  mb: 0.5
                                }}
                              >
                                {venta.producto}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: '0.8rem' }}
                              >
                                Cliente: {venta.cliente}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: '0.75rem', mt: 0.5 }}
                              >
                                📅 {dayjs(venta.fecha).format('DD/MM/YYYY')}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', ml: 1 }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: '#1976d2', 
                                  fontWeight: 'bold',
                                  fontSize: '1rem',
                                  lineHeight: 1.2
                                }}
                              >
                                {formatearMoneda(venta.total)}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#e65100',
                                  fontWeight: 'bold',
                                  fontSize: '0.75rem',
                                  display: 'block'
                                }}
                              >
                                💰 {formatearMoneda(calcularGananciaVenta(venta))}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: '0.75rem'
                                }}
                              >
                                Cant: {venta.cantidad}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 0.5,
                            mt: 1,
                            justifyContent: 'center'
                          }}>
                            <Tooltip title="Ver detalle">
                              <IconButton
                                size="small"
                                onClick={() => abrirVerVenta(venta)}
                                sx={{ color: '#c46494' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => abrirEditarVenta(venta)}
                                sx={{ color: '#2196f3' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                onClick={() => abrirEliminarVenta(venta)}
                                sx={{ color: '#f44336' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}

                {/* Mensaje cuando no hay ventas */}
                {getVentasAMostrar().length === 0 && (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    color: 'text.secondary'
                  }}>
                    <ShoppingCartIcon sx={{ fontSize: '3rem', mb: 1, opacity: 0.5 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {filtroFechas.activo ? 'No se encontraron ventas en el rango de fechas seleccionado' : 'No hay ventas registradas'}
                    </Typography>
                    <Typography variant="body2">
                      {filtroFechas.activo ? 'Intenta con un rango de fechas diferente' : 'Comienza agregando tu primera venta'}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Sección de Servicios */}
            <Grid item xs={12}>
              <Paper sx={{ 
                p: isMobile ? 1 : 1.5, 
                minHeight: isMobile ? 'auto' : '300px',
                height: 'auto'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'flex-start' : 'center', 
                  mb: 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 0
                }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ fontWeight: 'bold', color: '#9c27b0' }}
                  >
                    Servicios Realizados
                  </Typography>
                  <Button
                    variant="contained"
                    size={isMobile ? "medium" : "small"}
                    startIcon={<AddIcon />}
                    onClick={abrirModalServicio}
                    fullWidth={isMobile}
                    sx={{
                      backgroundColor: '#9c27b0',
                      '&:hover': { backgroundColor: '#7b1fa2' }
                    }}
                  >
                    Agregar Servicio
                  </Button>
                </Box>
                
                {/* Vista Desktop - Tabla */}
                {!isMobile ? (
                  <TableContainer sx={{ 
                    maxHeight: isMobile ? 'none' : '250px',
                    overflowX: 'auto'
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: '120px' }}><strong>Servicio</strong></TableCell>
                          <TableCell sx={{ minWidth: '120px' }}><strong>Paciente</strong></TableCell>
                          <TableCell sx={{ minWidth: '100px', textAlign: 'right' }}><strong>Precio</strong></TableCell>
                          <TableCell sx={{ minWidth: '120px' }}><strong>Veterinario</strong></TableCell>
                          <TableCell sx={{ minWidth: '100px', textAlign: 'center' }}><strong>Fecha</strong></TableCell>
                          <TableCell sx={{ minWidth: '140px', textAlign: 'center' }}><strong>Acciones</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getServiciosAMostrar().map((servicio) => (
                          <TableRow key={servicio.id}>
                            <TableCell sx={{ 
                              fontSize: '0.875rem',
                              wordBreak: 'break-word'
                            }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {servicio.tipoServicio}
                                </Typography>
                                {servicio.tipoServicio === 'Otro' && servicio.descripcionOtro && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: '#666', 
                                      fontStyle: 'italic',
                                      display: 'block',
                                      mt: 0.5
                                    }}
                                  >
                                    {servicio.descripcionOtro}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ 
                              fontSize: '0.875rem',
                              wordBreak: 'break-word'
                            }}>
                              {servicio.paciente || 'Sin paciente'}
                            </TableCell>
                            <TableCell sx={{ 
                              textAlign: 'right',
                              fontWeight: 'bold',
                              color: '#9c27b0'
                            }}>
                              {formatearMoneda(servicio.precio)}
                            </TableCell>
                            <TableCell sx={{ 
                              fontSize: '0.875rem',
                              wordBreak: 'break-word'
                            }}>
                              {servicio.veterinario}
                            </TableCell>
                            <TableCell sx={{ 
                              textAlign: 'center',
                              fontSize: '0.875rem'
                            }}>
                              {dayjs(servicio.fecha).format('DD/MM/YYYY')}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 0.5,
                                justifyContent: 'center'
                              }}>
                                <Tooltip title="Ver detalle">
                                  <IconButton
                                    size="small"
                                    onClick={() => abrirVerServicio(servicio)}
                                    sx={{ color: '#c46494' }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() => abrirEditarServicio(servicio)}
                                    sx={{ color: '#2196f3' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton
                                    size="small"
                                    onClick={() => abrirEliminarServicio(servicio)}
                                    sx={{ color: '#f44336' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  /* Vista Mobile - Cards */
                  <Box sx={{ 
                    maxHeight: '340px',
                    overflowY: 'auto',
                    pr: 0.5
                  }}>
                    {getServiciosAMostrar().map((servicio) => (
                      <Card key={servicio.id} sx={{ 
                        mb: 1.5, 
                        border: '1px solid #e0e0e0',
                        borderLeft: '4px solid #9c27b0'
                      }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 1
                          }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: 'bold', 
                                  color: '#9c27b0',
                                  lineHeight: 1.2,
                                  mb: 0.5
                                }}
                              >
                                {servicio.tipoServicio}
                              </Typography>
                              {servicio.tipoServicio === 'Otro' && servicio.descripcionOtro && (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#666', 
                                    fontStyle: 'italic',
                                    fontSize: '0.75rem',
                                    display: 'block',
                                    mb: 0.5
                                  }}
                                >
                                  📝 {servicio.descripcionOtro}
                                </Typography>
                              )}
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: '0.8rem' }}
                              >
                                Paciente: {servicio.paciente || 'Sin paciente'}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: '0.8rem' }}
                              >
                                Dr. {servicio.veterinario}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: '0.75rem', mt: 0.5 }}
                              >
                                📅 {dayjs(servicio.fecha).format('DD/MM/YYYY')}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', ml: 1 }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: '#7b1fa2', 
                                  fontWeight: 'bold',
                                  fontSize: '1rem',
                                  lineHeight: 1.2
                                }}
                              >
                                {formatearMoneda(servicio.precio)}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 0.5,
                            mt: 1,
                            justifyContent: 'center'
                          }}>
                            <Tooltip title="Ver detalle">
                              <IconButton
                                size="small"
                                onClick={() => abrirVerServicio(servicio)}
                                sx={{ color: '#c46494' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => abrirEditarServicio(servicio)}
                                sx={{ color: '#2196f3' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                onClick={() => abrirEliminarServicio(servicio)}
                                sx={{ color: '#f44336' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}

                {/* Mensaje cuando no hay servicios */}
                {getServiciosAMostrar().length === 0 && (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4,
                    color: 'text.secondary'
                  }}>
                    <ServiceIcon sx={{ fontSize: '3rem', mb: 1, opacity: 0.5 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {filtroFechas.activo ? 'No se encontraron servicios en el rango de fechas seleccionado' : 'No hay servicios registrados'}
                    </Typography>
                    <Typography variant="body2">
                      {filtroFechas.activo ? 'Intenta con un rango de fechas diferente' : 'Comienza agregando tu primer servicio'}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Sección de Adquisiciones de Productos */}
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: isMobile ? 0.5 : 1 }}>
            <Grid item xs={12}>
              <Paper sx={{ 
                p: isMobile ? 1 : 1.5, 
                minHeight: isMobile ? 'auto' : '300px',
                height: 'auto'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'flex-start' : 'center', 
                  mb: 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 0
                }}>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"} 
                    sx={{ fontWeight: 'bold', color: '#f44336' }}
                  >
                    Adquisiciones / Compra de Productos
                  </Typography>
                  <Button
                    variant="contained"
                    size={isMobile ? "medium" : "small"}
                    startIcon={<AddShoppingCartIcon />}
                    onClick={abrirModalAdquisicion}
                    fullWidth={isMobile}
                    sx={{
                      backgroundColor: '#f44336',
                      '&:hover': { backgroundColor: '#c62828' }
                    }}
                  >
                    Registrar Adquisición
                  </Button>
                </Box>

                {!isMobile ? (
                  <TableContainer sx={{ maxHeight: '250px', overflowX: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ minWidth: '140px' }}><strong>Producto</strong></TableCell>
                          <TableCell sx={{ minWidth: '70px', textAlign: 'center' }}><strong>Cant.</strong></TableCell>
                          <TableCell sx={{ minWidth: '110px', textAlign: 'right' }}><strong>Costo Unit.</strong></TableCell>
                          <TableCell sx={{ minWidth: '110px', textAlign: 'right' }}><strong>Total Gasto</strong></TableCell>
                          <TableCell sx={{ minWidth: '120px' }}><strong>Proveedor</strong></TableCell>
                          <TableCell sx={{ minWidth: '100px', textAlign: 'center' }}><strong>Fecha</strong></TableCell>
                          <TableCell sx={{ minWidth: '90px', textAlign: 'center' }}><strong>Acciones</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getAdquisicionesAMostrar().map((adq) => (
                          <TableRow key={adq.id}>
                            <TableCell sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>
                              <Box>
                                {adq.productoNombre}
                                {adq.esNuevoProducto && (
                                  <Chip label="Nuevo" size="small" sx={{ ml: 0.5, fontSize: '0.65rem', height: '18px', backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>{adq.cantidad}</TableCell>
                            <TableCell sx={{ textAlign: 'right', color: '#c62828' }}>{formatearMoneda(adq.costoUnitario)}</TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: '#c62828' }}>{formatearMoneda(adq.costoTotal)}</TableCell>
                            <TableCell sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>{adq.proveedor || '—'}</TableCell>
                            <TableCell sx={{ textAlign: 'center', fontSize: '0.875rem' }}>{dayjs(adq.fecha).format('DD/MM/YYYY')}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="Eliminar">
                                  <IconButton size="small" onClick={() => abrirEliminarAdquisicion(adq)} sx={{ color: '#f44336' }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ maxHeight: '340px', overflowY: 'auto', pr: 0.5 }}>
                    {getAdquisicionesAMostrar().map((adq) => (
                      <Card key={adq.id} sx={{ mb: 1.5, border: '1px solid #e0e0e0', borderLeft: '4px solid #f44336' }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#c62828', lineHeight: 1.2, mb: 0.5 }}>
                                {adq.productoNombre}
                                {adq.esNuevoProducto && (
                                  <Chip label="Nuevo" size="small" sx={{ ml: 0.5, fontSize: '0.6rem', height: '16px', backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                                )}
                              </Typography>
                              {adq.proveedor && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                  Proveedor: {adq.proveedor}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                📅 {dayjs(adq.fecha).format('DD/MM/YYYY')}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right', ml: 1 }}>
                              <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                                {formatearMoneda(adq.costoTotal)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                Cant: {adq.cantidad} × {formatearMoneda(adq.costoUnitario)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" onClick={() => abrirEliminarAdquisicion(adq)} sx={{ color: '#f44336' }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}

                {getAdquisicionesAMostrar().length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <InventoryIcon sx={{ fontSize: '3rem', mb: 1, opacity: 0.5 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {filtroFechas.activo ? 'No se encontraron adquisiciones en el rango seleccionado' : 'No hay adquisiciones registradas'}
                    </Typography>
                    <Typography variant="body2">
                      {filtroFechas.activo ? 'Intenta con un rango de fechas diferente' : 'Registra la compra de nuevos productos para el inventario'}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Sección de Ventas de Petshop */}
            <Grid item xs={12}>
              <VentasPetshopSection
                ventas={getVentasPetshopAMostrar()}
                productos={productosPetshop}
                filtroActivo={filtroFechas.activo}
                formatearMoneda={formatearMoneda}
                isMobile={isMobile}
                onRecargar={async () => {
                  await cargarPetshop();
                }}
                notificar={(message, severity = 'success') => setSnackbar({ open: true, message, severity })}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Modal para Venta de Producto */}
        <Dialog
          open={openProductoModal}
          onClose={() => setOpenProductoModal(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ pb: 1, backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }}>
            🛒 Registrar Venta de Productos
          </DialogTitle>
          <DialogContent sx={{ px: isMobile ? 1.5 : 3 }}>
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cliente *"
                  value={datosVenta.cliente}
                  onChange={(e) => setDatosVenta(prev => ({ ...prev, cliente: e.target.value }))}
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fecha *"
                  type="date"
                  value={datosVenta.fecha}
                  onChange={(e) => setDatosVenta(prev => ({ ...prev, fecha: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }}>
              <Chip label={`Productos (${datosVenta.items.length})`} sx={{ backgroundColor: '#e3f2fd', color: '#1565c0' }} />
            </Divider>

            {datosVenta.items.map((item, index) => (
              <Paper key={item.id} sx={{ p: isMobile ? 1.5 : 2, mb: 1.5, border: '1px solid #bbdefb', borderLeft: '4px solid #2196f3', backgroundColor: '#fafafa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    Producto #{index + 1}
                  </Typography>
                  {datosVenta.items.length > 1 && (
                    <IconButton size="small" onClick={() => quitarItemVenta(item.id)} sx={{ color: '#f44336' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Autocomplete
                      size="small"
                      options={[
                        { id: 'otro', label: '📝 Otro (Producto Personalizado)', stock: null, precio: null },
                        ...productos.map(p => ({ id: p.id, label: p.nombre, stock: p.cantidad || 0, precio: p.precio || 0 }))
                      ]}
                      getOptionLabel={(option) => option.label || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={
                        item.productoId === 'otro'
                          ? { id: 'otro', label: '📝 Otro (Producto Personalizado)', stock: null, precio: null }
                          : productos.find(p => p.id === item.productoId)
                            ? { id: item.productoId, label: productos.find(p => p.id === item.productoId).nombre, stock: productos.find(p => p.id === item.productoId).cantidad || 0, precio: productos.find(p => p.id === item.productoId).precio || 0 }
                            : null
                      }
                      onChange={(e, newValue) => actualizarItemVenta(item.id, 'productoId', newValue?.id || '')}
                      noOptionsText="No se encontraron productos"
                      renderInput={(params) => <TextField {...params} label="Producto *" />}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.875rem' }}>
                              {option.label}{option.precio !== null ? ` — ${formatearMoneda(option.precio)}` : ''}
                            </Typography>
                            {option.stock !== null && (
                              <Chip label={`Stock: ${option.stock}`} color={option.stock <= 5 ? 'error' : option.stock <= 10 ? 'warning' : 'success'} size="small" sx={{ ml: 1, flexShrink: 0 }} />
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                  {item.esProductoPersonalizado && (
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Nombre del Producto *"
                        value={item.nombreProductoPersonalizado}
                        onChange={(e) => actualizarItemVenta(item.id, 'nombreProductoPersonalizado', e.target.value)}
                        placeholder="Ingresa el nombre del producto..." />
                    </Grid>
                  )}
                  <Grid item xs={4} sm={3}>
                    <TextField fullWidth size="small" label="Cantidad" type="number"
                      value={item.cantidad}
                      onChange={(e) => actualizarItemVenta(item.id, 'cantidad', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      inputProps={{ min: 1 }} />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField fullWidth size="small" label="Precio Unit." type="number"
                      value={item.precioUnitario}
                      onChange={(e) => actualizarItemVenta(item.id, 'precioUnitario', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      InputProps={{ startAdornment: '$' }} />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField fullWidth size="small" label="Subtotal"
                      value={formatearMoneda(item.total)}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiInputBase-root': { backgroundColor: '#e3f2fd' } }} />
                  </Grid>
                  {!item.esProductoPersonalizado && item.productoId && (
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth size="small" label="Stock disponible"
                        value={productos.find(p => p.id === item.productoId)?.cantidad || 0}
                        InputProps={{ readOnly: true }}
                        helperText="En inventario" />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}

            <Button startIcon={<AddIcon />} onClick={agregarItemVenta} sx={{ color: '#2196f3', mb: 2 }} size="small">
              Agregar otro producto
            </Button>

            {datosVenta.items.length > 0 && (
              <Box sx={{ textAlign: 'right', p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                  Total: {formatearMoneda(datosVenta.items.reduce((s, i) => s + (i.total || 0), 0))}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: isMobile ? 1.5 : 3, pb: isMobile ? 2 : 1, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button onClick={() => setOpenProductoModal(false)} fullWidth={isMobile}>Cancelar</Button>
            <Button onClick={guardarVentaProducto} variant="contained" startIcon={<SaveIcon />} disabled={loadingVenta} fullWidth={isMobile}
              sx={{ backgroundColor: '#2196f3', '&:hover': { backgroundColor: '#1976d2' } }}>
              {loadingVenta ? 'Guardando...' : `Guardar Venta${datosVenta.items.length > 1 ? 's' : ''}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal para Servicio */}
        <Dialog
          open={openServicioModal}
          onClose={() => setOpenServicioModal(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ pb: 1, backgroundColor: '#f3e5f5', color: '#6a1b9a', fontWeight: 'bold' }}>
            🏥 Registrar Servicio(s)
          </DialogTitle>
          <DialogContent sx={{ px: isMobile ? 1.5 : 3 }}>
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Veterinario *" value={datosServicio.veterinario}
                  onChange={(e) => setDatosServicio(prev => ({ ...prev, veterinario: e.target.value }))}
                  size={isMobile ? 'small' : 'medium'} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Fecha *" type="date" value={datosServicio.fecha}
                  onChange={(e) => setDatosServicio(prev => ({ ...prev, fecha: e.target.value }))}
                  InputLabelProps={{ shrink: true }} size={isMobile ? 'small' : 'medium'} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Notas generales" value={datosServicio.notas}
                  onChange={(e) => setDatosServicio(prev => ({ ...prev, notas: e.target.value }))}
                  size={isMobile ? 'small' : 'medium'} placeholder="Notas opcionales..." />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }}>
              <Chip label={`Servicios (${datosServicio.items.length})`} sx={{ backgroundColor: '#f3e5f5', color: '#6a1b9a' }} />
            </Divider>

            {datosServicio.items.map((item, index) => (
              <Paper key={item.id} sx={{ p: isMobile ? 1.5 : 2, mb: 1.5, border: '1px solid #e1bee7', borderLeft: '4px solid #9c27b0', backgroundColor: '#fafafa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                    Servicio #{index + 1}
                  </Typography>
                  {datosServicio.items.length > 1 && (
                    <IconButton size="small" onClick={() => quitarItemServicio(item.id)} sx={{ color: '#f44336' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo de Servicio *</InputLabel>
                      <Select value={item.tipoServicio}
                        onChange={(e) => actualizarItemServicio(item.id, 'tipoServicio', e.target.value)}
                        label="Tipo de Servicio *">
                        {tiposServicios.map((tipo) => (
                          <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      size="small"
                      options={[
                        ...(item.tipoServicio === 'Otro' ? [{ id: '', label: 'Ninguno', sub: '' }] : []),
                        ...pacientes.map(p => ({ id: p.id, label: p.nombre, sub: p.propietario || 'Sin propietario' }))
                      ]}
                      getOptionLabel={(option) => option.label || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={
                        item.pacienteId === ''
                          ? (item.tipoServicio === 'Otro' ? { id: '', label: 'Ninguno', sub: '' } : null)
                          : pacientes.find(p => p.id === item.pacienteId)
                            ? { id: item.pacienteId, label: pacientes.find(p => p.id === item.pacienteId).nombre, sub: pacientes.find(p => p.id === item.pacienteId).propietario || 'Sin propietario' }
                            : null
                      }
                      onChange={(e, newValue) => actualizarItemServicio(item.id, 'pacienteId', newValue?.id ?? '')}
                      noOptionsText="No se encontraron pacientes"
                      renderInput={(params) => <TextField {...params} label={item.tipoServicio === 'Otro' ? 'Paciente (opcional)' : 'Paciente *'} />}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id || 'ninguno'}>
                          <Box>
                            <Typography sx={{ fontSize: '0.875rem', fontStyle: option.id === '' ? 'italic' : 'normal' }}>{option.label}</Typography>
                            {option.sub && option.id !== '' && <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{option.sub}</Typography>}
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                  {item.tipoServicio === 'Otro' && (
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Descripción del servicio"
                        value={item.descripcionOtro}
                        onChange={(e) => actualizarItemServicio(item.id, 'descripcionOtro', e.target.value)}
                        placeholder="Describe el servicio realizado..." />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Precio *" type="number"
                      value={item.precio}
                      onChange={(e) => actualizarItemServicio(item.id, 'precio', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      InputProps={{ startAdornment: '$' }} />
                  </Grid>
                </Grid>
              </Paper>
            ))}

            <Button startIcon={<AddIcon />} onClick={agregarItemServicio} sx={{ color: '#9c27b0', mb: 2 }} size="small">
              Agregar otro servicio
            </Button>

            {datosServicio.items.length > 0 && (
              <Box sx={{ textAlign: 'right', p: 1.5, bgcolor: '#f3e5f5', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ color: '#6a1b9a', fontWeight: 'bold' }}>
                  Total: {formatearMoneda(datosServicio.items.reduce((s, i) => s + (parseFloat(i.precio) || 0), 0))}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: isMobile ? 1.5 : 3, pb: isMobile ? 2 : 1, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button onClick={() => setOpenServicioModal(false)} fullWidth={isMobile}>Cancelar</Button>
            <Button onClick={guardarServicio} variant="contained" startIcon={<SaveIcon />} disabled={loadingServicio} fullWidth={isMobile}
              sx={{ backgroundColor: '#9c27b0', '&:hover': { backgroundColor: '#7b1fa2' } }}>
              {loadingServicio ? 'Guardando...' : `Guardar Servicio${datosServicio.items.length > 1 ? 's' : ''}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal para Editar Venta de Producto */}
        <Dialog
          open={openEditProductoModal}
          onClose={() => setOpenEditProductoModal(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ pb: isMobile ? 1 : 2 }}>
            Editar Venta de Producto
          </DialogTitle>
          <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={productos.map(p => ({ id: p.id, label: p.nombre, stock: p.cantidad || 0, precio: p.precio || 0 }))}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={productos.find(p => p.id === ventaProducto.productoId)
                    ? { id: ventaProducto.productoId, label: productos.find(p => p.id === ventaProducto.productoId).nombre, stock: productos.find(p => p.id === ventaProducto.productoId).cantidad || 0, precio: productos.find(p => p.id === ventaProducto.productoId).precio || 0 }
                    : null
                  }
                  onChange={(e, newValue) => handleProductoChange('productoId', newValue?.id || '')}
                  noOptionsText="No se encontraron productos"
                  renderInput={(params) => <TextField {...params} label="Producto" />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Typography>{option.label} — {formatearMoneda(option.precio)}</Typography>
                        <Chip label={`Stock: ${option.stock}`} color={option.stock <= 5 ? 'error' : option.stock <= 10 ? 'warning' : 'success'} size="small" sx={{ ml: 1, flexShrink: 0 }} />
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Cantidad"
                  type="number"
                  value={ventaProducto.cantidad}
                  onChange={(e) => handleProductoChange('cantidad', e.target.value)}
                  inputProps={{ 
                    min: 1,
                    max: ventaProducto.productoId ? (productos.find(p => p.id === ventaProducto.productoId)?.cantidad || 0) + (itemToEdit?.cantidad || 0) : undefined
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Stock Disponible"
                  value={ventaProducto.productoId ? (productos.find(p => p.id === ventaProducto.productoId)?.cantidad || 0) + (itemToEdit?.cantidad || 0) : 0}
                  InputProps={{ readOnly: true }}
                  helperText="Incluye cantidad actual"
                />
              </Grid>
              
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Precio Unitario"
                  type="number"
                  value={ventaProducto.precioUnitario}
                  onChange={(e) => handleProductoChange('precioUnitario', e.target.value)}
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cliente"
                  value={ventaProducto.cliente}
                  onChange={(e) => handleProductoChange('cliente', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Fecha"
                  type="date"
                  value={ventaProducto.fecha}
                  onChange={(e) => handleProductoChange('fecha', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Total"
                  value={formatearMoneda(ventaProducto.total)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditProductoModal(false)}>Cancelar</Button>
            <Button 
              onClick={editarVentaProducto} 
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loadingVenta}
            >
              {loadingVenta ? 'Actualizando...' : 'Actualizar Venta'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal para Editar Servicio */}
        <Dialog 
          open={openEditServicioModal} 
          onClose={() => setOpenEditServicioModal(false)} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ pb: isMobile ? 1 : 2 }}>
            Editar Servicio
          </DialogTitle>
          <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Servicio</InputLabel>
                  <Select
                    value={nuevoServicio.tipoServicio}
                    onChange={(e) => handleServicioChange('tipoServicio', e.target.value)}
                    label="Tipo de Servicio"
                  >
                    {tiposServicios.map((tipo) => (
                      <MenuItem key={tipo} value={tipo}>
                        {tipo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Autocomplete
                  options={pacientes.map(p => ({ id: p.id, label: p.nombre, sub: p.propietario || 'Sin propietario' }))}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={pacientes.find(p => p.id === nuevoServicio.pacienteId)
                    ? { id: nuevoServicio.pacienteId, label: pacientes.find(p => p.id === nuevoServicio.pacienteId).nombre, sub: pacientes.find(p => p.id === nuevoServicio.pacienteId).propietario || 'Sin propietario' }
                    : null
                  }
                  onChange={(e, newValue) => handleServicioChange('pacienteId', newValue?.id || '')}
                  noOptionsText="No se encontraron pacientes"
                  renderInput={(params) => <TextField {...params} label="Paciente" />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography sx={{ fontSize: '0.875rem' }}>{option.label}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{option.sub}</Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Precio"
                  type="number"
                  value={nuevoServicio.precio}
                  onChange={(e) => handleServicioChange('precio', e.target.value)}
                  onFocus={(e) => e.target.select()} // Seleccionar todo el texto al hacer focus
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Veterinario"
                  value={nuevoServicio.veterinario}
                  onChange={(e) => handleServicioChange('veterinario', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Fecha"
                  type="date"
                  value={nuevoServicio.fecha}
                  onChange={(e) => handleServicioChange('fecha', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notas (opcional)"
                  multiline
                  rows={3}
                  value={nuevoServicio.notas}
                  onChange={(e) => handleServicioChange('notas', e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditServicioModal(false)}>Cancelar</Button>
            <Button 
              onClick={editarServicio} 
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loadingServicio}
            >
              {loadingServicio ? 'Actualizando...' : 'Actualizar Servicio'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal para Ver Detalle */}
        <Dialog 
          open={openViewModal} 
          onClose={() => setOpenViewModal(false)} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            pb: 1, 
            backgroundColor: viewType === 'venta' ? '#e3f2fd' : '#f3e5f5',
            color: viewType === 'venta' ? '#1976d2' : '#7b1fa2',
            fontWeight: 'bold'
          }}>
            {viewType === 'venta' ? '🛒 Detalle de Venta' : '🏥 Detalle de Servicio'}
          </DialogTitle>
          <DialogContent sx={{ px: isMobile ? 2 : 3, pt: 2 }}>
            {itemToView && viewType === 'venta' && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    {itemToView.producto || itemToView.productoNombre}
                  </Typography>
                  <Chip 
                    label={dayjs(itemToView.fecha).format('DD/MM/YYYY')}
                    size="small"
                    sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Cantidad</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{itemToView.cantidad}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Precio Unitario</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatearMoneda(itemToView.precioUnitario)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Cliente</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{itemToView.cliente}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Total Venta</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>{formatearMoneda(itemToView.total)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Costo de Compra (unit.)</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                      {getCostoUnitario(itemToView) !== null
                        ? formatearMoneda(getCostoUnitario(itemToView))
                        : 'Sin costo registrado'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">💰 Ganancia</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                      {formatearMoneda(calcularGananciaVenta(itemToView))}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
            {itemToView && viewType === 'servicio' && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                    {itemToView.tipoServicio}
                  </Typography>
                  <Chip 
                    label={dayjs(itemToView.fecha).format('DD/MM/YYYY')}
                    size="small"
                    sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
                  />
                </Box>
                {itemToView.tipoServicio === 'Otro' && itemToView.descripcionOtro && (
                  <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic', color: '#666' }}>
                    📝 {itemToView.descripcionOtro}
                  </Typography>
                )}
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Paciente</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{itemToView.paciente || itemToView.pacienteNombre || 'Sin paciente'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Veterinario</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{itemToView.veterinario}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Precio del Servicio</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>{formatearMoneda(itemToView.precio)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">💰 Ganancia</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e65100' }}>{formatearMoneda(itemToView.precio)}</Typography>
                  </Grid>
                  {itemToView.notas && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Notas</Typography>
                      <Typography variant="body2">{itemToView.notas}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 2 : 1 }}>
            <Button onClick={() => setOpenViewModal(false)} variant="contained" sx={{ backgroundColor: '#c46494', '&:hover': { backgroundColor: '#a0537a' } }}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Confirmación de Eliminación */}
        <Dialog 
          open={openDeleteModal} 
          onClose={() => setOpenDeleteModal(false)} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ pb: isMobile ? 1 : 2 }}>
            Confirmar Eliminación
          </DialogTitle>
          <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Esta acción no se puede deshacer
            </Alert>
            <Typography>
              ¿Está seguro que desea eliminar {deleteType === 'venta' ? 'esta venta' : deleteType === 'adquisicion' ? 'esta adquisición' : 'este servicio'}?
            </Typography>
            {itemToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                {deleteType === 'venta' ? (
                  <>
                    <Typography><strong>Producto:</strong> {itemToDelete.producto || itemToDelete.productoNombre}</Typography>
                    <Typography><strong>Cantidad:</strong> {itemToDelete.cantidad}</Typography>
                    <Typography><strong>Cliente:</strong> {itemToDelete.cliente}</Typography>
                    <Typography><strong>Total:</strong> {formatearMoneda(itemToDelete.total)}</Typography>
                    {itemToDelete.productoId && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Se restaurarán {itemToDelete.cantidad} unidades al inventario
                      </Alert>
                    )}
                  </>
                ) : deleteType === 'adquisicion' ? (
                  <>
                    <Typography><strong>Producto:</strong> {itemToDelete.productoNombre}</Typography>
                    <Typography><strong>Cantidad:</strong> {itemToDelete.cantidad}</Typography>
                    <Typography><strong>Total Gasto:</strong> {formatearMoneda(itemToDelete.costoTotal)}</Typography>
                    {itemToDelete.proveedor && <Typography><strong>Proveedor:</strong> {itemToDelete.proveedor}</Typography>}
                    {itemToDelete.productoId && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Se descontarán {itemToDelete.cantidad} unidades del inventario
                      </Alert>
                    )}
                  </>
                ) : (
                  <>
                    <Typography><strong>Servicio:</strong> {itemToDelete.tipoServicio}</Typography>
                    <Typography><strong>Paciente:</strong> {itemToDelete.paciente || itemToDelete.pacienteNombre}</Typography>
                    <Typography><strong>Veterinario:</strong> {itemToDelete.veterinario}</Typography>
                    <Typography><strong>Precio:</strong> {formatearMoneda(itemToDelete.precio)}</Typography>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteModal(false)} startIcon={<CancelIcon />}>
              Cancelar
            </Button>
            <Button 
              onClick={confirmarEliminacion}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={loadingDelete}
            >
              {loadingDelete ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal para Adquisición de Producto */}
        <Dialog
          open={openAdquisicionModal}
          onClose={() => setOpenAdquisicionModal(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ pb: 1, backgroundColor: '#ffebee', color: '#c62828', fontWeight: 'bold' }}>
            🛒 Registrar Adquisición de Productos
          </DialogTitle>
          <DialogContent sx={{ px: isMobile ? 1.5 : 3 }}>
            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Proveedor (opcional)" value={datosAdquisicion.proveedor}
                  onChange={(e) => setDatosAdquisicion(prev => ({ ...prev, proveedor: e.target.value }))}
                  size={isMobile ? 'small' : 'medium'} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Fecha *" type="date" value={datosAdquisicion.fecha}
                  onChange={(e) => setDatosAdquisicion(prev => ({ ...prev, fecha: e.target.value }))}
                  InputLabelProps={{ shrink: true }} size={isMobile ? 'small' : 'medium'} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Notas (opcional)" value={datosAdquisicion.notas}
                  onChange={(e) => setDatosAdquisicion(prev => ({ ...prev, notas: e.target.value }))}
                  size={isMobile ? 'small' : 'medium'} placeholder="Notas opcionales..." />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }}>
              <Chip label={`Productos (${datosAdquisicion.items.length})`} sx={{ backgroundColor: '#ffebee', color: '#c62828' }} />
            </Divider>

            {datosAdquisicion.items.map((item, index) => (
              <Paper key={item.id} sx={{ p: isMobile ? 1.5 : 2, mb: 1.5, border: '1px solid #ffcdd2', borderLeft: '4px solid #f44336', backgroundColor: '#fafafa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#c62828' }}>
                    Producto #{index + 1}
                  </Typography>
                  {datosAdquisicion.items.length > 1 && (
                    <IconButton size="small" onClick={() => quitarItemAdq(item.id)} sx={{ color: '#f44336' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo</InputLabel>
                      <Select value={item.esNuevoProducto ? 'nuevo' : 'existente'}
                        onChange={(e) => actualizarItemAdq(item.id, 'esNuevoProducto', e.target.value === 'nuevo')}
                        label="Tipo">
                        <MenuItem value="nuevo">➕ Nuevo producto</MenuItem>
                        <MenuItem value="existente">📦 Producto existente</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    {item.esNuevoProducto ? (
                      <TextField fullWidth size="small" label="Nombre del Producto *"
                        value={item.productoNombre}
                        onChange={(e) => actualizarItemAdq(item.id, 'productoNombre', e.target.value)}
                        placeholder="Nombre del nuevo producto..." />
                    ) : (
                      <Autocomplete
                        size="small"
                        options={productos.map(p => ({ id: p.id, label: p.nombre, stock: p.cantidad || 0 }))}
                        getOptionLabel={(option) => option.label || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={productos.find(p => p.id === item.productoId)
                          ? { id: item.productoId, label: productos.find(p => p.id === item.productoId).nombre, stock: productos.find(p => p.id === item.productoId).cantidad || 0 }
                          : null
                        }
                        onChange={(e, newValue) => actualizarItemAdq(item.id, 'productoId', newValue?.id || '')}
                        noOptionsText="No se encontraron productos"
                        renderInput={(params) => <TextField {...params} label="Producto del inventario *" />}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                              <Typography sx={{ fontSize: '0.875rem' }}>{option.label}</Typography>
                              <Chip label={`Stock: ${option.stock}`} color={option.stock <= 5 ? 'error' : option.stock <= 10 ? 'warning' : 'success'} size="small" sx={{ ml: 1, flexShrink: 0 }} />
                            </Box>
                          </li>
                        )}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" label="Cantidad *" type="number"
                      value={item.cantidad}
                      onChange={(e) => actualizarItemAdq(item.id, 'cantidad', e.target.value)}
                      onFocus={(e) => e.target.select()} inputProps={{ min: 1 }} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" label="Costo Unitario *" type="number"
                      value={item.costoUnitario}
                      onChange={(e) => actualizarItemAdq(item.id, 'costoUnitario', e.target.value)}
                      onFocus={(e) => e.target.select()} InputProps={{ startAdornment: '$' }} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth size="small" label="Total"
                      value={formatearMoneda(item.costoTotal)}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiInputBase-root': { backgroundColor: '#ffebee' } }} />
                  </Grid>
                </Grid>
              </Paper>
            ))}

            <Button startIcon={<AddIcon />} onClick={agregarItemAdq} sx={{ color: '#f44336', mb: 2 }} size="small">
              Agregar otro producto
            </Button>

            {datosAdquisicion.items.length > 0 && (
              <Box sx={{ textAlign: 'right', p: 1.5, bgcolor: '#ffebee', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                  Total Gasto: {formatearMoneda(datosAdquisicion.items.reduce((s, i) => s + (i.costoTotal || 0), 0))}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: isMobile ? 1.5 : 3, pb: isMobile ? 2 : 1, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button onClick={() => setOpenAdquisicionModal(false)} fullWidth={isMobile}>Cancelar</Button>
            <Button onClick={guardarAdquisicion} variant="contained" startIcon={<SaveIcon />} disabled={loadingAdquisicion} fullWidth={isMobile}
              sx={{ backgroundColor: '#f44336', '&:hover': { backgroundColor: '#c62828' } }}>
              {loadingAdquisicion ? 'Guardando...' : `Registrar Adquisición${datosAdquisicion.items.length > 1 ? 'es' : ''}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>


      </Container>
    </AdminLayout>
  );
};

export default Finanzas;